'use client';
/**
 * ════════════════════════════════════════════════════════════════════════════
 *  MOTION ENVIRONMENT — the three questions every animation must ask
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Rules 1 and 2 of the motion contract (`docs/design-refactor/BRIEF.md`) are
 * not per-component decisions, they are environment reads, and they are wrong
 * in the same way every time somebody re-implements them:
 *
 *   - `matchMedia('(prefers-reduced-motion: reduce)').matches` sampled once at
 *     mount misses the user flipping the OS setting while the tab is open.
 *   - An `IntersectionObserver` alone keeps a fullscreen shader running at
 *     60fps in a background tab, because a hidden tab's elements are still
 *     "intersecting". `visibilitychange` is the other half and is always
 *     forgotten.
 *   - Neither exists on the server, and `useState(() => matchMedia(...))`
 *     throws there.
 *
 * So all three live here, as `useSyncExternalStore` subscriptions, and every
 * component in `src/motion/` reads them from this module.
 *
 * The server snapshot for reduced motion is `true` on purpose: SSR emits the
 * settled, legible, zero-motion markup, and the client upgrades it after
 * hydration. The Astro site is the SEO surface — what the crawler sees must be
 * the finished state, never frame zero of an animation.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
/* ------------------------------------------------------------------ */
/*  prefers-reduced-motion                                             */
/* ------------------------------------------------------------------ */
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
function mediaQueryList(query) {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
        return null;
    try {
        return window.matchMedia(query);
    }
    catch {
        return null;
    }
}
/** `addEventListener` on a `MediaQueryList` is Safari 14+; `addListener` is the rest. */
function onMediaChange(mql, handler) {
    if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }
    if (typeof mql.addListener === 'function') {
        mql.addListener(handler);
        return () => mql.removeListener(handler);
    }
    return () => undefined;
}
function subscribeReducedMotion(onStoreChange) {
    const mql = mediaQueryList(REDUCED_MOTION_QUERY);
    if (!mql)
        return () => undefined;
    return onMediaChange(mql, onStoreChange);
}
function readReducedMotion() {
    return mediaQueryList(REDUCED_MOTION_QUERY)?.matches ?? false;
}
function readReducedMotionOnServer() {
    return true;
}
/**
 * `true` when the user has asked the platform for less motion.
 *
 * Every component treats this as "render the end state, then stop": shader
 * time freezes at a composed constant, GSAP timelines are seeked to 1,
 * autoplay never starts. Never as "render nothing".
 */
export function useReducedMotion() {
    return useSyncExternalStore(subscribeReducedMotion, readReducedMotion, readReducedMotionOnServer);
}
/* ------------------------------------------------------------------ */
/*  Page visibility                                                    */
/* ------------------------------------------------------------------ */
function subscribePageVisible(onStoreChange) {
    if (typeof document === 'undefined')
        return () => undefined;
    document.addEventListener('visibilitychange', onStoreChange);
    return () => document.removeEventListener('visibilitychange', onStoreChange);
}
function readPageVisible() {
    if (typeof document === 'undefined')
        return true;
    return document.visibilityState !== 'hidden';
}
function readPageVisibleOnServer() {
    return true;
}
/** `false` while the tab is backgrounded or the window is minimised. */
export function usePageVisible() {
    return useSyncExternalStore(subscribePageVisible, readPageVisible, readPageVisibleOnServer);
}
/**
 * Whether `ref`'s element is in the viewport.
 *
 * Defaults to `true` when `IntersectionObserver` is missing (old Safari,
 * jsdom): a component that cannot observe must still be usable, and the
 * `visibilitychange` half of the gate still applies.
 */
export function useInView(ref, { rootMargin = '160px', threshold = 0, once = false } = {}) {
    const [inView, setInView] = useState(false);
    const seenRef = useRef(false);
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        if (typeof IntersectionObserver === 'undefined') {
            seenRef.current = true;
            setInView(true);
            return;
        }
        const observer = new IntersectionObserver((entries) => {
            const entry = entries[entries.length - 1];
            if (!entry)
                return;
            if (entry.isIntersecting) {
                seenRef.current = true;
                setInView(true);
                if (once)
                    observer.disconnect();
            }
            else if (!once) {
                setInView(false);
            }
        }, { rootMargin, threshold });
        observer.observe(el);
        return () => observer.disconnect();
    }, [ref, rootMargin, threshold, once]);
    return inView;
}
/**
 * The gate every `requestAnimationFrame` loop in this folder is wrapped in:
 * on screen **and** the tab is in front.
 */
export function useMotionActive(ref, options) {
    const inView = useInView(ref, options);
    const pageVisible = usePageVisible();
    return inView && pageVisible;
}
/**
 * The *computed* direction of a mounted element, not `<html dir>`.
 *
 * `src/icons/direction.tsx` deliberately reads the document instead, because an
 * icon that flips one frame after paint is worse than one that assumes the
 * page direction. Motion is the opposite case: a slider whose drag sign is
 * decided a frame late is invisible (nobody has dragged it yet), while a
 * slider inside a `dir="ltr"` island — a latin gallery, an embedded chart —
 * that inherits the page's RTL is wrong forever. So here we measure.
 *
 * Rule 8: mirror the axis, never the glyphs.
 */
export function useResolvedDirection(ref, override) {
    const [dir, setDir] = useState(override ?? 'rtl');
    useEffect(() => {
        if (override) {
            setDir(override);
            return;
        }
        const el = ref.current;
        if (!el || typeof window === 'undefined' || typeof window.getComputedStyle !== 'function')
            return;
        const read = () => {
            const value = window.getComputedStyle(el).direction;
            setDir(value === 'ltr' ? 'ltr' : 'rtl');
        };
        read();
        if (typeof MutationObserver === 'undefined')
            return;
        const observer = new MutationObserver(read);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['dir', 'lang'] });
        let closest = el;
        while (closest && closest !== document.documentElement) {
            observer.observe(closest, { attributes: true, attributeFilter: ['dir'] });
            closest = closest.parentElement;
        }
        return () => observer.disconnect();
    }, [ref, override]);
    return dir;
}
/** `+1` in LTR, `-1` in RTL — the sign a "next" gesture moves along X. */
export function directionSign(dir) {
    return dir === 'rtl' ? -1 : 1;
}
//# sourceMappingURL=env.js.map